import React, { useState, useEffect } from 'react';
import { 
    Box, Typography, TextField, Grid, Card, CardContent, CircularProgress, Stack,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton,
    Button, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, 
    InputLabel, Select, MenuItem, Checkbox, ListItemText, OutlinedInput
} from '@mui/material';
import { ChevronLeftRounded, ChevronRightRounded, SaveRounded, FlashOnRounded } from '@mui/icons-material';
import { doc, getDoc, collection, query, onSnapshot, addDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import Menu from '../../panel/Menu';

// Helper to format local date to YYYY-MM-DD
const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

function RateUpdates() {
    const [loading, setLoading] = useState(true);
    const [propertyId, setPropertyId] = useState("");
    const [authConfig, setAuthConfig] = useState({ hotelCode: "", authCode: "" });
    const [otaMeta, setOtaMeta] = useState({ otas: [], roomMappings: [] });
    const [savedUpdates, setSavedUpdates] = useState([]);
    
    // Calendar View State
    const [viewStartDate, setViewStartDate] = useState(() => formatDate(new Date()));

    // Grid Edit State (Stores all modified rates)
    const [pendingEdits, setPendingEdits] = useState({});
    const [isSavingAll, setIsSavingAll] = useState(false);

    // Bulk Update Modal States
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkRoomId, setBulkRoomId] = useState("");
    const [bulkPlanId, setBulkPlanId] = useState("");
    const [bulkOtas, setBulkOtas] = useState([]);
    const [bulkStartDate, setBulkStartDate] = useState("");
    const [bulkEndDate, setBulkEndDate] = useState("");
    const [bulkPrice, setBulkPrice] = useState("");
    const [isBulkSyncing, setIsBulkSyncing] = useState(false);

    useEffect(() => {
        const fetchUser = async () => {
            const user = auth.currentUser;
            if (user) {
                const snap = await getDoc(doc(db, "authroles", user.uid));
                if (snap.exists()) setPropertyId(snap.data().propertyId);
            }
        };
        fetchUser();
    }, []);

    useEffect(() => {
        if (!propertyId) return;

        const q = query(collection(db, "ota_rate_updates"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const updates = snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(item => item.propertyId === propertyId);
            setSavedUpdates(updates);
        });

        const fetchInventoryAndOTAs = async () => {
            try {
                const configSnap = await getDoc(doc(db, "channel_manager", propertyId));
                let hotelConfig = { hotelCode: "", authCode: "" };
                
                if (configSnap.exists()) {
                    hotelConfig = configSnap.data();
                    setAuthConfig({ hotelCode: hotelConfig.hotelCode, authCode: hotelConfig.authCode });
                    
                    const res = await fetch('https://navajowhite-wren-495848.hostingersite.com/api/ota-info', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ HotelCode: hotelConfig.hotelCode, AuthCode: hotelConfig.authCode })
                    });
                    const data = await res.json();
                    
                    const invSnap = await getDoc(doc(db, "ota_room_inventory", propertyId));
                    const rooms = invSnap.exists() ? invSnap.data().rooms : [];

                    if (data.APIResponse?.IsSuccess) {
                        setOtaMeta({ otas: data.OTAs || [], roomMappings: rooms });
                    }
                }
            } catch (err) { console.error(err); }
            finally { setLoading(false); }
        };

        fetchInventoryAndOTAs();
        return () => unsubscribe();
    }, [propertyId]);

    const calendarDates = Array.from({ length: 7 }).map((_, i) => {
        const d = new Date(viewStartDate);
        d.setDate(d.getDate() + i);
        return formatDate(d);
    });

    const shiftDate = (days) => {
        const d = new Date(viewStartDate);
        d.setDate(d.getDate() + days);
        setViewStartDate(formatDate(d));
    };

    const getRateForDate = (roomId, planId, ota, targetDate) => {
        // Force string comparison to avoid Number vs String mismatch bugs
        const applicable = savedUpdates.filter(u =>
            String(u.roomId) === String(roomId) &&
            String(u.ratePlanId) === String(planId) &&
            String(u.otaCode) === String(ota) &&
            u.startDate <= targetDate &&
            u.endDate >= targetDate
        );

        if (applicable.length === 0) return "-";
        
        applicable.sort((a, b) => {
            // Safely handle Firestore timestamps vs normal dates
            const timeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : (a.createdAt ? new Date(a.createdAt).getTime() : 0);
            const timeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : (b.createdAt ? new Date(b.createdAt).getTime() : 0);
            return timeB - timeA;
        });

        return applicable[0].price.toString();
    };

    // Handle individual cell typing
    const handleCellChange = (cellKey, originalRate, newValue) => {
        if (newValue === originalRate) {
            // If user reverts to original rate, remove from pending edits
            const newEdits = { ...pendingEdits };
            delete newEdits[cellKey];
            setPendingEdits(newEdits);
        } else {
            // Store modified rate
            setPendingEdits(prev => ({ ...prev, [cellKey]: newValue }));
        }
    };

    // GLOBAL SAVE LOGIC (Saves all edited cells at once)
    const handleSaveAll = async () => {
        const validEdits = Object.entries(pendingEdits).filter(([_, val]) => val.trim() !== "");
        if (validEdits.length === 0) return;

        setIsSavingAll(true);
        try {
            const ratePayload = [];
            const firebaseDocsToSave = []; // Stage DB records instead of saving immediately

            for (const [key, newRate] of validEdits) {
                // Key format: RoomId-PlanId-OTACode-YYYY-MM-DD
                const [roomId, planId, otaCode, ...dateParts] = key.split('-');
                const targetDate = dateParts.join('-');

                const room = otaMeta.roomMappings.find(r => String(r.RoomId) === roomId);
                const plan = room?.RoomRatePlans?.find(p => String(p.RatePlanId) === planId);
                const ota = otaMeta.otas.find(o => o.OTACode === otaCode);

                ratePayload.push({
                    FromDate: targetDate,
                    ToDate: targetDate,
                    RoomId: roomId,
                    RatePlanId: planId,
                    Rate: newRate,
                    ExtraAdultPriceSpecified: "true",
                    ExtraChildPriceSpecified: "false",
                    OTASpecified: "true",
                    OTACode: otaCode
                });

                firebaseDocsToSave.push({
                    propertyId,
                    roomId: roomId,
                    roomName: room?.RoomName || "Unknown",
                    ratePlanId: planId,
                    ratePlanName: plan?.RatePlanName || "EP",
                    price: newRate, 
                    startDate: targetDate, 
                    endDate: targetDate, 
                    otaCode: otaCode,
                    otaName: ota?.OTAName || otaCode,
                    status: "Synced",
                    createdAt: serverTimestamp(),
                    syncedAt: new Date().toISOString()
                });
            }

            // Execute API push first
            const response = await fetch('https://navajowhite-wren-495848.hostingersite.com/api/rate-update', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    Authentication: { HotelCode: authConfig.hotelCode, AuthCode: authConfig.authCode },
                    Rates: { Rate: ratePayload }
                })
            });

            const result = await response.json();
            
            if (result.APIResponse?.IsSuccess) {
                // Only save to Firebase if the API returns success
                await Promise.all(firebaseDocsToSave.map(docData => 
                    addDoc(collection(db, "ota_rate_updates"), docData)
                ));
                
                setPendingEdits({}); // Clear highlighted edits
                alert("All rates successfully updated and synced!");
            } else {
                alert("OTA API Update Failed: " + (result.APIResponse?.Message || "Unknown error"));
            }
        } catch (err) {
            console.error("Save Error:", err);
            alert("Failed to save changes. Please try again.");
        } finally {
            setIsSavingAll(false);
        }
    };

    // BULK SAVE LOGIC
    const resetBulkForm = () => {
        setBulkRoomId("");
        setBulkPlanId("");
        setBulkOtas([]);
        setBulkStartDate("");
        setBulkEndDate("");
        setBulkPrice("");
    };

    const handleBulkSave = async () => {
        if (!bulkRoomId || !bulkPlanId || !bulkPrice || !bulkStartDate || !bulkEndDate || bulkOtas.length === 0) {
            return alert("Please fill all fields.");
        }

        setIsBulkSyncing(true);
        const room = otaMeta.roomMappings.find(r => String(r.RoomId) === String(bulkRoomId));
        const plan = room?.RoomRatePlans?.find(p => String(p.RatePlanId) === String(bulkPlanId));

        try {
            for (const otaCode of bulkOtas) {
                const ratePayload = [{
                    FromDate: bulkStartDate,
                    ToDate: bulkEndDate,
                    RoomId: bulkRoomId.toString(),
                    RatePlanId: bulkPlanId.toString(),
                    Rate: bulkPrice.toString(),
                    ExtraAdultPriceSpecified: "true",
                    ExtraChildPriceSpecified: "false",
                    OTASpecified: "true",
                    OTACode: otaCode
                }];

                const response = await fetch('https://navajowhite-wren-495848.hostingersite.com/api/rate-update', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        Authentication: { HotelCode: authConfig.hotelCode, AuthCode: authConfig.authCode },
                        Rates: { Rate: ratePayload }
                    })
                });

                const result = await response.json();
                
                if (result.APIResponse?.IsSuccess) {
                    await addDoc(collection(db, "ota_rate_updates"), {
                        propertyId,
                        roomId: bulkRoomId.toString(),
                        roomName: room?.RoomName || "Unknown",
                        ratePlanId: bulkPlanId.toString(),
                        ratePlanName: plan?.RatePlanName || "EP",
                        price: bulkPrice, 
                        startDate: bulkStartDate, 
                        endDate: bulkEndDate, 
                        otaCode: otaCode,
                        otaName: otaMeta.otas.find(o => o.OTACode === otaCode)?.OTAName || otaCode,
                        status: "Synced",
                        createdAt: serverTimestamp(),
                        syncedAt: new Date().toISOString()
                    });
                }
            }
            alert("Bulk rates successfully synced to OTAs!");
            setBulkOpen(false);
            resetBulkForm();
        } catch (err) { 
            alert("Bulk update failed. Please try again."); 
        } finally {
            setIsBulkSyncing(false);
        }
    };

    if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={30} /></Box>;

    return (
        <Box sx={{ display: 'flex', bgcolor: "#F1F5F9", minHeight: "100vh" }}>
            <Menu />
            <Box className="content-wrapper" sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>Direct Rates Calendar</Typography>
                        <Typography variant="caption" color="text.secondary">Update rates directly in the grid and click save all, or use bulk update.</Typography>
                    </Box>
                    <Stack direction="row" spacing={2} alignItems="center">
                        {Object.keys(pendingEdits).length > 0 && (
                            <Button 
                                variant="contained" 
                                color="success"
                                onClick={handleSaveAll}
                                disabled={isSavingAll}
                                startIcon={isSavingAll ? <CircularProgress size={16} color="inherit" /> : <SaveRounded />}
                                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                            >
                                {isSavingAll ? "Saving..." : `Save All Changes (${Object.keys(pendingEdits).length})`}
                            </Button>
                        )}
                        <Button 
                            variant="contained" 
                            color="primary" 
                            startIcon={<FlashOnRounded />}
                            onClick={() => setBulkOpen(true)}
                            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, bgcolor: '#0F172A' }}
                        >
                            Bulk Update
                        </Button>
                    </Stack>
                </Stack>

                <Grid className="row" spacing={2}>
                    <Grid className="col-sm-12" item xs={12}>
                        <Card sx={{ borderRadius: 3, border: '1px solid #E2E8F0', boxShadow: 'none', overflow: 'hidden' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>7-Day Rate Outlook</Typography>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <IconButton size="small" onClick={() => shiftDate(-7)}><ChevronLeftRounded /></IconButton>
                                    <Typography variant="body2" sx={{ fontWeight: 700, px: 2 }}>
                                        {new Date(viewStartDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                                    </Typography>
                                    <IconButton size="small" onClick={() => shiftDate(7)}><ChevronRightRounded /></IconButton>
                                </Stack>
                            </Box>
                            
                            <TableContainer sx={{ maxHeight: 'calc(100vh - 200px)' }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', width: '20%', bgcolor: '#F8FAFC', borderRight: '1px solid #E2E8F0', zIndex: 3 }}>
                                                ROOM / PLAN / OTA
                                            </TableCell>
                                            {calendarDates.map(date => {
                                                const d = new Date(date);
                                                return (
                                                    <TableCell key={date} align="center" sx={{ fontWeight: 700, fontSize: '0.75rem', bgcolor: '#F8FAFC', minWidth: 100, zIndex: 2 }}>
                                                        {d.toLocaleDateString('en-US', { weekday: 'short' })}<br/>
                                                        <span style={{color: '#64748B'}}>{d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {otaMeta.roomMappings.map((room) => (
                                            room.RoomRatePlans?.map((plan) => (
                                                otaMeta.otas.map((ota) => (
                                                    <TableRow key={`${room.RoomId}-${plan.RatePlanId}-${ota.OTACode}`} hover>
                                                        <TableCell sx={{ borderRight: '1px solid #F1F5F9' }}>
                                                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 800, color: '#0F172A' }}>{room.RoomName}</Typography>
                                                            <Typography variant="caption" sx={{ display: 'block', color: '#64748B', fontSize: '0.65rem' }}>{plan.RatePlanName}</Typography>
                                                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: '#6366F1', fontSize: '0.65rem' }}>{ota.OTAName}</Typography>
                                                        </TableCell>
                                                        
                                                        {calendarDates.map(date => {
                                                            const originalRate = getRateForDate(room.RoomId, plan.RatePlanId, ota.OTACode, date);
                                                            const cellKey = `${room.RoomId}-${plan.RatePlanId}-${ota.OTACode}-${date}`;
                                                            
                                                            // Determine which rate to show: pending edit OR saved original
                                                            const displayRate = pendingEdits[cellKey] !== undefined ? pendingEdits[cellKey] : (originalRate !== "-" ? originalRate : "");
                                                            const isEdited = pendingEdits[cellKey] !== undefined;
                                                            
                                                            return (
                                                                <TableCell key={date} align="center" sx={{ borderRight: '1px solid #F1F5F9', p: 1 }}>
                                                                    <TextField 
                                                                        size="small"
                                                                        variant="outlined"
                                                                        placeholder="-"
                                                                        value={displayRate}
                                                                        onChange={(e) => handleCellChange(cellKey, originalRate !== "-" ? originalRate : "", e.target.value)}
                                                                        sx={{ 
                                                                            width: 70, 
                                                                            bgcolor: isEdited ? '#FFFBEB' : 'transparent', // Highlight edited cells
                                                                            '& .MuiInputBase-input': { 
                                                                                p: '4px 8px', 
                                                                                fontSize: '0.75rem', 
                                                                                textAlign: 'center', 
                                                                                fontWeight: isEdited ? 800 : 600,
                                                                                color: isEdited ? '#D97706' : 'inherit'
                                                                            },
                                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                                borderColor: isEdited ? '#FCD34D' : 'rgba(0,0,0,0.23)'
                                                                            }
                                                                        }}
                                                                    />
                                                                </TableCell>
                                                            );
                                                        })}
                                                    </TableRow>
                                                ))
                                            ))
                                        ))}
                                        {otaMeta.roomMappings.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                                    <Typography color="text.secondary">No Room Mappings Found</Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* BULK UPDATE MODAL */}
            <Dialog open={bulkOpen} onClose={() => !isBulkSyncing && setBulkOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 800, borderBottom: '1px solid #E2E8F0', mb: 2 }}>
                    Bulk Update Rates
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Room Type</InputLabel>
                            <Select value={bulkRoomId} label="Room Type" onChange={(e) => setBulkRoomId(e.target.value)}>
                                {otaMeta.roomMappings.map((room) => (
                                    <MenuItem key={room.RoomId} value={room.RoomId}>{room.RoomName}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth size="small" disabled={!bulkRoomId}>
                            <InputLabel>Rate Plan</InputLabel>
                            <Select value={bulkPlanId} label="Rate Plan" onChange={(e) => setBulkPlanId(e.target.value)}>
                                {otaMeta.roomMappings.find(r => String(r.RoomId) === String(bulkRoomId))?.RoomRatePlans?.map((plan) => (
                                    <MenuItem key={plan.RatePlanId} value={plan.RatePlanId}>{plan.RatePlanName}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth size="small">
                            <InputLabel>Target OTAs</InputLabel>
                            <Select multiple value={bulkOtas} onChange={(e) => setBulkOtas(e.target.value)} input={<OutlinedInput label="Target OTAs" />} renderValue={(s) => s.map(code => otaMeta.otas.find(o => o.OTACode === code)?.OTAName).join(', ')}>
                                {otaMeta.otas.map((ota) => (
                                    <MenuItem key={ota.OTACode} value={ota.OTACode}>
                                        <Checkbox size="small" checked={bulkOtas.indexOf(ota.OTACode) > -1} />
                                        <ListItemText primary={ota.OTAName} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Stack direction="row" spacing={2}>
                            <TextField fullWidth size="small" type="date" label="Start Date" InputLabelProps={{ shrink: true }} value={bulkStartDate} onChange={e => setBulkStartDate(e.target.value)} />
                            <TextField fullWidth size="small" type="date" label="End Date" InputLabelProps={{ shrink: true }} value={bulkEndDate} onChange={e => setBulkEndDate(e.target.value)} />
                        </Stack>

                        <TextField fullWidth size="small" label="New Rate (₹)" type="number" value={bulkPrice} onChange={e => setBulkPrice(e.target.value)} />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 0 }}>
                    <Button onClick={() => setBulkOpen(false)} color="inherit" disabled={isBulkSyncing}>Cancel</Button>
                    <Button 
                        variant="contained" 
                        onClick={handleBulkSave} 
                        disabled={isBulkSyncing}
                        startIcon={isBulkSyncing && <CircularProgress size={16} color="inherit" />}
                        sx={{ bgcolor: '#0F172A', textTransform: 'none' }}
                    >
                        {isBulkSyncing ? "Syncing..." : "Update & Sync"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default RateUpdates;